import i18n from '@/i18n';
import {
  clampOutpaintScale,
  validateOutpaintScale,
} from '@/pages/ipe/workbench/utils/outpaintExpandScale';

/** AI 扩图 · 自定义宽高比（比例整数，非像素） */
export const OUTPAINT_CUSTOM_AR_MIN = 1;
export const OUTPAINT_CUSTOM_AR_MAX = 99;
export const OUTPAINT_CUSTOM_AR_MAX_LONG_SHORT = 10;

/**
 * 将自定义比例单边限制在 1～99。
 * @param {number|string} value 输入值
 * @returns {number}
 */
export function clampOutpaintAspectSide(value) {
  const v = Math.floor(Number(value) || 0);
  if (v < OUTPAINT_CUSTOM_AR_MIN) return OUTPAINT_CUSTOM_AR_MIN;
  if (v > OUTPAINT_CUSTOM_AR_MAX) return OUTPAINT_CUSTOM_AR_MAX;
  return v;
}

/**
 * 最大公约数（欧几里得）。
 * @param {number} a
 * @param {number} b
 * @returns {number}
 */
export function gcdOutpaintAspect(a, b) {
  let x = Math.abs(Math.floor(Number(a) || 0));
  let y = Math.abs(Math.floor(Number(b) || 0));
  if (x < 1) x = 1;
  if (y < 1) y = 1;
  while (y !== 0) {
    const t = y;
    y = x % y;
    x = t;
  }
  return x || 1;
}

/**
 * 钳制后 GCD 约分，得到提交给灵图 ar 的最简整数比。
 * @param {number|string} width
 * @param {number|string} height
 * @returns {{ outputWidth: number, outputHeight: number }}
 */
export function normalizeOutpaintCustomAspect(width, height) {
  let w = Math.max(OUTPAINT_CUSTOM_AR_MIN, Math.floor(Number(width) || 0));
  let h = Math.max(OUTPAINT_CUSTOM_AR_MIN, Math.floor(Number(height) || 0));
  const g = gcdOutpaintAspect(w, h);
  w = Math.max(OUTPAINT_CUSTOM_AR_MIN, Math.floor(w / g));
  h = Math.max(OUTPAINT_CUSTOM_AR_MIN, Math.floor(h / g));
  return { outputWidth: w, outputHeight: h };
}

/**
 * 校验约分后的自定义比例是否合法。
 * @param {number} width
 * @param {number} height
 * @returns {string|null} 错误文案，合法时返回 null
 */
export function validateOutpaintCustomAspectRatio(width, height) {
  const w = Math.floor(Number(width) || 0);
  const h = Math.floor(Number(height) || 0);
  if (
    w < OUTPAINT_CUSTOM_AR_MIN
    || w > OUTPAINT_CUSTOM_AR_MAX
    || h < OUTPAINT_CUSTOM_AR_MIN
    || h > OUTPAINT_CUSTOM_AR_MAX
  ) {
    return i18n.t('workbench:ipeUtilError.customArRange', { min: OUTPAINT_CUSTOM_AR_MIN, max: OUTPAINT_CUSTOM_AR_MAX });
  }
  const max = Math.max(w, h);
  const min = Math.min(w, h);
  if (min > 0 && max / min > OUTPAINT_CUSTOM_AR_MAX_LONG_SHORT) {
    return i18n.t('workbench:ipeUtilError.customArLongShort', { max: OUTPAINT_CUSTOM_AR_MAX_LONG_SHORT });
  }
  return null;
}

/**
 * 扩图提交前：倍数或自定义比例校验与规范化。
 * @param {object} expandParams
 * @returns {{ ok: true, expandParams: object } | { ok: false, message: string }}
 */
export function prepareOutpaintExpandParamsForSubmit(expandParams) {
  const mode = Number(expandParams?.expandMode) === 2 ? 2 : 1;
  if (mode === 1) {
    const scaleMessage = validateOutpaintScale(expandParams?.scale);
    if (scaleMessage) {
      return { ok: false, message: scaleMessage };
    }
    return {
      ok: true,
      expandParams: {
        ...expandParams,
        scale: clampOutpaintScale(expandParams?.scale),
      },
    };
  }
  if (String(expandParams?.aspectRatio || '') !== 'custom') {
    return { ok: true, expandParams: { ...expandParams } };
  }
  const { outputWidth, outputHeight } = normalizeOutpaintCustomAspect(
    expandParams.outputWidth,
    expandParams.outputHeight,
  );
  const message = validateOutpaintCustomAspectRatio(outputWidth, outputHeight);
  if (message) {
    return { ok: false, message };
  }
  return {
    ok: true,
    expandParams: {
      ...expandParams,
      outputWidth,
      outputHeight,
    },
  };
}
